/**
 * SharePoint Browser Dialog Component
 * A dialog wrapper for the SharePointBrowser component
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { SharePointBrowser } from './SharePointBrowser';
import type { SharePointBrowserDialogProps } from './types/sharepoint';

export const SharePointBrowserDialog: React.FC<SharePointBrowserDialogProps> = ({
  open,
  maxWidth = 'md',
  fullScreen: fullScreenProp = false,
  ...browserProps
}) => {
  const { showTitle: showTitleProp, ...restBrowserProps } = browserProps;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreen = fullScreenProp || isMobile;

  const handleClose = () => {
    if (browserProps.onCancel) {
      browserProps.onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {browserProps.title || 'Browse SharePoint'}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '100%' }}>
        <Box sx={{ height: '100%' }}>
          <SharePointBrowser
            {...restBrowserProps}
            showTitle={showTitleProp ?? false}
            onCancel={undefined} // Remove cancel from browser as we handle it in dialog
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
